import type { ModelDefinition } from "@/models.js";

/**
 * Built-in voices for the legacy tts-1 / tts-1-hd models. The first entry
 * ("alloy") is the default when the caller omits `voice`.
 */
const OPENAI_TTS_VOICES = [
	"alloy",
	"ash",
	"coral",
	"echo",
	"fable",
	"nova",
	"onyx",
	"sage",
	"shimmer",
];

/**
 * Built-in voices for gpt-4o-mini-tts, which supports a broader catalog than
 * the legacy tts-1 models.
 */
const OPENAI_GPT4O_MINI_TTS_VOICES = [
	"alloy",
	"ash",
	"ballad",
	"cedar",
	"coral",
	"echo",
	"fable",
	"marin",
	"nova",
	"onyx",
	"sage",
	"shimmer",
	"verse",
];

/**
 * Built-in voices for the gpt-realtime family. This list is distinct from the
 * TTS voice catalogues above; the realtime API does not accept every TTS
 * voice. The first entry ("marin") is the default when the caller omits a
 * voice.
 */
const OPENAI_REALTIME_VOICES = [
	"marin",
	"cedar",
	"alloy",
	"ash",
	"ballad",
	"coral",
	"echo",
	"sage",
	"shimmer",
	"verse",
];

export const openaiModels = [
	{
		id: "gpt-4o-mini",
		name: "GPT-4o Mini",
		description:
			"Affordable small model for fast, lightweight tasks with text and vision capabilities.",
		family: "openai",
		releasedAt: new Date("2024-07-18"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-mini",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				cachedInputPrice: "0.075e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportsN: true,
			},
			{
				providerId: "azure",
				externalId: "gpt-4o-mini",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				cachedInputPrice: "0.075e-6",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2026-01-09"),
				deactivatedAt: new Date("2026-03-31"),
			},
		],
	},
	{
		id: "gpt-4o-search-preview",
		name: "GPT-4o Search Preview",
		description:
			"GPT-4o with native web search capabilities for up-to-date information.",
		family: "openai",
		releasedAt: new Date("2024-10-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-search-preview",
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				webSearch: true,
				webSearchPrice: "0.025", // $25 per 1000 searches for non-reasoning models
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: false, // Search models don't support additional tools
				jsonOutput: false,
				supportedParameters: ["max_tokens", "response_format"],
			},
		],
	},
	{
		id: "gpt-4o-mini-search-preview",
		name: "GPT-4o Mini Search Preview",
		description:
			"Cost-effective GPT-4o Mini with native web search capabilities.",
		family: "openai",
		releasedAt: new Date("2024-10-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-mini-search-preview",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				webSearch: true,
				webSearchPrice: "0.025", // $25 per 1000 searches for non-reasoning models
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: false, // Search models don't support additional tools
				jsonOutput: false,
				supportedParameters: ["max_tokens", "response_format"],
			},
		],
	},
	{
		id: "gpt-4",
		name: "GPT-4",
		description:
			"Original GPT-4 model with strong reasoning and tool use capabilities.",
		family: "openai",
		releasedAt: new Date("2023-03-14"),
		providers: [
			{
				test: "skip",
				providerId: "openai",
				externalId: "gpt-4",
				inputPrice: "30.0e-6",
				outputPrice: "60.0e-6",
				requestPrice: "0",
				contextSize: 8192,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4",
				inputPrice: "30.0e-6",
				outputPrice: "60.0e-6",
				contextSize: 8192,
				maxOutput: 8192,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "gpt-4o",
		name: "GPT-4o",
		description:
			"Multimodal flagship model with vision, fast responses, and tool support.",
		family: "openai",
		releasedAt: new Date("2024-05-13"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o",
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "1.25e-6",
				requestPrice: "0",
				imageInputPrice: "0.00553",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportsN: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4o",
				deactivatedAt: new Date("2027-04-14"),
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "1.25e-6",
				imageInputPrice: "0.00553",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-3.5-turbo",
		name: "GPT-3.5 Turbo",
		description:
			"Fast and cost-effective model for simple tasks and conversational applications.",
		family: "openai",
		releasedAt: new Date("2022-11-30"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-3.5-turbo",
				inputPrice: "0.5e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 16385,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutputSchema: false,
				jsonOutput: true,
				supportsN: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-35-turbo",
				inputPrice: "0.5e-6",
				outputPrice: "1.5e-6",
				contextSize: 16385,
				maxOutput: 4096,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-4-turbo",
		name: "GPT-4 Turbo",
		description:
			"Enhanced GPT-4 with vision capabilities and improved performance.",
		family: "openai",
		releasedAt: new Date("2023-11-06"),
		providers: [
			{
				test: "skip",
				providerId: "openai",
				externalId: "gpt-4-turbo",
				inputPrice: "10.0e-6",
				outputPrice: "30.0e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4-turbo",
				inputPrice: "10.0e-6",
				outputPrice: "30.0e-6",
				contextSize: 128000,
				maxOutput: 4096,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-4.1",
		name: "GPT-4.1",
		description: "Updated GPT-4 with vision support and parallel tool calls.",
		family: "openai",
		releasedAt: new Date("2025-04-14"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4.1",
				inputPrice: "2.0e-6",
				outputPrice: "8.0e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportsN: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4.1",
				deactivatedAt: new Date("2027-04-14"),
				inputPrice: "2.0e-6",
				outputPrice: "8.0e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "o1",
		name: "o1",
		description:
			"Advanced reasoning model that thinks before responding for complex problem-solving.",
		family: "openai",
		releasedAt: new Date("2024-09-12"),
		providers: [
			{
				test: "skip",
				providerId: "openai",
				externalId: "o1",
				inputPrice: "15.0e-6",
				outputPrice: "60.0e-6",
				cachedInputPrice: "7.5e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "o1",
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "15.0e-6",
				outputPrice: "60.0e-6",
				cachedInputPrice: "7.5e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "o1-mini",
		name: "o1 Mini",
		description:
			"Smaller, faster reasoning model optimized for coding and math tasks.",
		family: "openai",
		releasedAt: new Date("2024-09-12"),
		providers: [
			{
				stability: "unstable" as const,
				providerId: "openai",
				externalId: "o1-mini",
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.55e-6",
				requestPrice: "0",
				deactivatedAt: new Date("2025-10-27"),
				contextSize: 128000,
				maxOutput: undefined,
				streaming: false,
				vision: false,
				tools: false,
				reasoning: false,
				supportsResponsesApi: false,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "o1-mini",
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.55e-6",
				requestPrice: "0",
				deactivatedAt: new Date("2025-10-27"),
				contextSize: 128000,
				maxOutput: undefined,
				streaming: false,
				vision: false,
				tools: false,
				reasoning: false,
				supportsResponsesApi: false,
				jsonOutput: false,
			},
		],
		supportsSystemRole: false,
	},
	{
		id: "gpt-4.1-mini",
		name: "GPT-4.1 Mini",
		description: "Compact version of GPT-4.1 with vision and tool support.",
		family: "openai",
		releasedAt: new Date("2025-04-14"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4.1-mini",
				inputPrice: "0.4e-6",
				outputPrice: "1.6e-6",
				cachedInputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportsN: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4.1-mini",
				deactivatedAt: new Date("2027-04-14"),
				inputPrice: "0.4e-6",
				outputPrice: "1.6e-6",
				cachedInputPrice: "0.1e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-4.1-nano",
		name: "GPT-4.1 Nano",
		description:
			"Ultra-lightweight GPT-4.1 variant for high-volume, cost-sensitive applications.",
		family: "openai",
		releasedAt: new Date("2025-04-14"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4.1-nano",
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportsN: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-4.1-nano",
				deactivatedAt: new Date("2026-10-14"),
				inputPrice: "0.1e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				parallelToolCalls: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "o3",
		name: "o3",
		description:
			"Next-generation reasoning model with enhanced problem-solving capabilities.",
		family: "openai",
		releasedAt: new Date("2025-06-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "o3",
				deactivatedAt: new Date("2026-12-10"),
				inputPrice: "2e-6",
				outputPrice: "8e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportedParameters: ["reasoning_effort", "response_format"],
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "o3",
				// Azure runs its own schedule and retires o3 seven weeks BEFORE
				// OpenAI's 2026-12-10 first-party shutdown above.
				deactivatedAt: new Date("2026-10-21"),
				inputPrice: "2e-6",
				outputPrice: "8e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "o3-mini",
		name: "o3 Mini",
		description:
			"Compact o3 reasoning model balancing performance and cost for complex tasks.",
		family: "openai",
		releasedAt: new Date("2025-06-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "o3-mini",
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.55e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
				supportedParameters: ["reasoning_effort", "response_format"],
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "o3-mini",
				deactivatedAt: new Date("2026-10-01"),
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.55e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: false,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "o4-mini",
		name: "o4 Mini",
		description:
			"Compact reasoning model with strong performance and efficient inference.",
		family: "openai",
		releasedAt: new Date("2025-04-16"),
		providers: [
			{
				providerId: "openai",
				externalId: "o4-mini",
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.275e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutputSchema: true,
				jsonOutput: true,
				supportedParameters: ["reasoning_effort", "response_format"],
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "o4-mini",
				deactivatedAt: new Date("2026-10-16"),
				inputPrice: "1.1e-6",
				outputPrice: "4.4e-6",
				cachedInputPrice: "0.275e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-oss-120b",
		name: "GPT OSS 120B",
		description:
			"Open-source 120B parameter model with reasoning capabilities via Groq inference.",
		family: "openai",
		releasedAt: new Date("2025-08-05"),
		providers: [
			{
				providerId: "groq",
				externalId: "openai/gpt-oss-120b",
				inputPrice: "0.15e-6",
				outputPrice: "0.75e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32766,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutput: true,
			},
			{
				// Consistently times out in CI due to model size
				providerId: "cerebras",
				stability: "unstable",
				externalId: "gpt-oss-120b",
				inputPrice: "0.35e-6",
				outputPrice: "0.75e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
				supportedParameters: [
					"temperature",
					"max_tokens",
					"top_p",
					"response_format",
					"tools",
					"tool_choice",
				],
			},
			{
				test: "skip",
				providerId: "nanogpt",
				externalId: "openai/gpt-oss-120b",
				inputPrice: "0.05e-6",
				outputPrice: "0.25e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32766,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
			},
			{
				providerId: "bytedance",
				externalId: "gpt-oss-120b-250805",
				inputPrice: "0.1e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 32000,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: false, // BytePlus doesn't support response_format
			},
			{
				providerId: "nebius",
				externalId: "openai/gpt-oss-120b",
				// Streaming tool calls arrive malformed from nebius (vLLM emits a
				// bogus tool_call index on the final argument fragment)
				stability: "unstable",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "fp4",
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "openai/gpt-oss-120b",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				// Together validates reasoning_effort on the gpt-oss route and 400s
				// ("Input validation error") on anything outside low/medium/high.
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutput: false,
			},
			{
				providerId: "azure",
				externalId: "gpt-oss-120b",
				inputPrice: "0.15e-6",
				outputPrice: "0.6e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				// Azure's gpt-oss-120b rejects tool_choice="required" (and named
				// function choices) with UnsupportedToolUse.
				supportedToolChoices: ["auto", "none"],
				reasoning: true,
				// Azure's gpt-oss-120b accepts response_format but wraps the output
				// in a `{"final": "..."}` envelope instead of the requested schema.
				jsonOutput: false,
			},
			{
				providerId: "runware",
				externalId: "openai-gpt-oss-120b",
				inputPrice: "0.032e-6",
				outputPrice: "0.14e-6",
				cachedInputPrice: "0.032e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "bf16",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "scx-ai",
				externalId: "gpt-oss-120b",
				inputPrice: "0.17e-6",
				outputPrice: "0.55e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				quantization: "fp8",
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-oss-20b",
		name: "GPT OSS 20B",
		description:
			"Lightweight open-source 20B model with reasoning support for efficient inference.",
		family: "openai",
		releasedAt: new Date("2025-08-05"),
		providers: [
			{
				providerId: "groq",
				externalId: "openai/gpt-oss-20b",
				inputPrice: "0.1e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32766,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "nanogpt",
				externalId: "openai/gpt-oss-20b",
				inputPrice: "0.04e-6",
				outputPrice: "0.15e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32766,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "openai/gpt-oss-20b",
				inputPrice: "0.05e-6",
				outputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: 32768,
				streaming: true,
				vision: false,
				tools: true,
				reasoning: true,
				// Together validates reasoning_effort on the gpt-oss route and 400s
				// ("Input validation error") on anything outside low/medium/high.
				reasoningEfforts: ["low", "medium", "high"],
				jsonOutput: false,
			},
		],
	},
	{
		id: "gpt-5",
		name: "GPT-5",
		description:
			"Latest flagship model with reasoning, vision, and enhanced capabilities.",
		family: "openai",
		releasedAt: new Date("2025-08-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5",
				deactivatedAt: new Date("2026-12-10"),
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5",
				// Azure runs its own schedule and keeps this past OpenAI's
				// 2026-12-10 first-party shutdown above.
				deactivatedAt: new Date("2027-02-09"),
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				supportsResponsesApi: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5-mini",
		name: "GPT-5 Mini",
		description:
			"Compact GPT-5 variant with reasoning and vision at reduced cost.",
		family: "openai",
		releasedAt: new Date("2025-08-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5-mini",
				deactivatedAt: new Date("2026-12-10"),
				inputPrice: "0.25e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5-mini",
				// Azure runs its own schedule and keeps this past OpenAI's
				// 2026-12-10 first-party shutdown above.
				deactivatedAt: new Date("2027-02-09"),
				inputPrice: "0.25e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				supportsResponsesApi: true,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5-nano",
		name: "GPT-5 Nano",
		description:
			"Ultra-efficient GPT-5 variant for high-volume applications with reasoning support.",
		family: "openai",
		releasedAt: new Date("2025-08-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5-nano",
				deactivatedAt: new Date("2026-12-10"),
				inputPrice: "0.05e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.005e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				streaming: true,
				vision: false,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5-nano",
				// Azure runs its own schedule and keeps this past OpenAI's
				// 2026-12-10 first-party shutdown above.
				deactivatedAt: new Date("2027-02-09"),
				inputPrice: "0.05e-6",
				outputPrice: "0.4e-6",
				cachedInputPrice: "0.005e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				reasoning: true,
				reasoningEfforts: ["minimal", "low", "medium", "high"],
				supportsResponsesApi: true,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"tools",
					"tool_choice",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5-chat-latest",
		name: "GPT-5 Chat Latest",
		description:
			"GPT-5 optimized for conversational interactions without tool calling.",
		family: "openai",
		releasedAt: new Date("2025-08-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5-chat-latest",
				// Shut down by OpenAI on 2026-07-23 (announced 2026-04-22);
				// requests now 404 with "has been deprecated". Replacement is
				// gpt-5.6-sol.
				deactivatedAt: new Date("2026-07-23"),
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: false,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.1",
		name: "GPT-5.1",
		description:
			"Iterative improvement on GPT-5 with enhanced reasoning and tool support.",
		family: "openai",
		releasedAt: new Date("2025-11-01"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.1",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				reasoning: true,
				reasoningEfforts: ["none", "minimal", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.1",
				deactivatedAt: new Date("2027-05-15"),
				inputPrice: "1.25e-6",
				outputPrice: "10.0e-6",
				cachedInputPrice: "0.125e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["none", "minimal", "low", "medium", "high", "xhigh"],
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5-pro",
		name: "GPT-5 Pro",
		description:
			"Premium GPT-5 tier with extended output limits and advanced reasoning for complex tasks.",
		family: "openai",
		releasedAt: new Date("2025-08-01"),
		providers: [
			{
				test: "skip",
				providerId: "openai",
				externalId: "gpt-5-pro",
				deactivatedAt: new Date("2026-12-10"),
				inputPrice: "15e-6",
				outputPrice: "120.0e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 272000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["high"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.1-codex",
		name: "GPT-5.1 Codex",
		description:
			"GPT-5.1 specialized for code generation and software development tasks.",
		family: "openai",
		releasedAt: new Date("2025-11-13"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.1-codex",
				// Shut down by OpenAI on 2026-07-23 (announced 2026-04-22);
				// requests now 404 with "has been deprecated". Replacement is
				// gpt-5.6-sol. The azure mapping below stays active — Azure runs
				// its own schedule and does not retire this until 2027-05-15.
				deactivatedAt: new Date("2026-07-23"),
				serviceTiers: ["priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "1.25e-6",
				outputPrice: "10e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 272000,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.1-codex",
				deactivatedAt: new Date("2027-05-15"),
				inputPrice: "1.25e-6",
				outputPrice: "10e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 272000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.1-codex-mini",
		name: "GPT-5.1 Codex mini",
		description:
			"Lightweight code-focused model for efficient software development workflows.",
		family: "openai",
		releasedAt: new Date("2025-11-12"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.1-codex-mini",
				// Shut down by OpenAI on 2026-07-23 (announced 2026-04-22);
				// requests now 404 with "has been deprecated". Replacement is
				// gpt-5.6-terra. The azure mapping below stays active — Azure runs
				// its own schedule and does not retire this until 2027-05-15.
				deactivatedAt: new Date("2026-07-23"),
				inputPrice: "0.25e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.1-codex-mini",
				deactivatedAt: new Date("2027-05-15"),
				inputPrice: "0.25e-6",
				outputPrice: "2e-6",
				cachedInputPrice: "0.025e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.2",
		name: "GPT-5.2",
		description: "Latest GPT-5 model with enhanced reasoning and tool support.",
		family: "openai",
		releasedAt: new Date("2025-12-11"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.2",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.2",
				deactivatedAt: new Date("2027-06-08"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.2-pro",
		name: "GPT-5.2 Pro",
		description:
			"Premium GPT-5.2 model with extended output limits and advanced reasoning for complex tasks.",
		family: "openai",
		releasedAt: new Date("2025-12-11"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.2-pro",
				inputPrice: "21.0e-6",
				outputPrice: "168.0e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 272000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				verbosity: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.2-pro",
				inputPrice: "21.0e-6",
				outputPrice: "168.0e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 272000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["medium", "high", "xhigh"],
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.2-chat-latest",
		name: "GPT-5.2 Chat",
		description:
			"GPT-5.2 Chat is the ChatGPT production snapshot for chat use. Use it to try the latest chat-focused updates.",
		family: "openai",
		releasedAt: new Date("2025-12-11"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.2-chat-latest",
				deprecatedAt: new Date("2026-05-13"),
				deactivatedAt: new Date("2026-08-10"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16400,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01", // $10 per 1000 searches for reasoning models
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.2-chat-latest",
				deprecatedAt: new Date("2026-05-13"),
				deactivatedAt: new Date("2026-08-10"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16400,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "gpt-5.4",
		name: "GPT-5.4",
		description:
			"Most capable model for complex professional work with reasoning support.",
		family: "openai",
		releasedAt: new Date("2026-03-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.4",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "2.5e-6",
				outputPrice: "15.0e-6",
				cachedInputPrice: "0.25e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.4",
				deactivatedAt: new Date("2027-09-02"),
				inputPrice: "2.5e-6",
				outputPrice: "15.0e-6",
				cachedInputPrice: "0.25e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.4-pro",
		name: "GPT-5.4 Pro",
		description:
			"Version of GPT-5.4 that uses more compute for smarter and more precise responses.",
		family: "openai",
		releasedAt: new Date("2026-03-01"),
		providers: [
			{
				providerId: "openai",
				test: "skip",
				externalId: "gpt-5.4-pro",
				serviceTiers: ["flex"],
				inputPrice: "30.0e-6",
				outputPrice: "180.0e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				verbosity: true,
				jsonOutput: true,
			},
			{
				providerId: "azure",
				test: "skip",
				externalId: "gpt-5.4-pro",
				deactivatedAt: new Date("2027-09-07"),
				inputPrice: "30.0e-6",
				outputPrice: "180.0e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.4-mini",
		name: "GPT-5.4 Mini",
		description:
			"Strong mini model for coding, computer use, and subagents in high-volume workloads.",
		family: "openai",
		releasedAt: new Date("2026-03-17"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.4-mini",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "0.75e-6",
				outputPrice: "4.5e-6",
				cachedInputPrice: "0.075e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.4-mini",
				deactivatedAt: new Date("2027-09-21"),
				inputPrice: "0.75e-6",
				outputPrice: "4.5e-6",
				cachedInputPrice: "0.075e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.4-nano",
		name: "GPT-5.4 Nano",
		description:
			"Cheapest GPT-5.4-class model for simple high-volume tasks like classification and data extraction.",
		family: "openai",
		releasedAt: new Date("2026-03-17"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.4-nano",
				serviceTiers: ["flex"],
				inputPrice: "0.2e-6",
				outputPrice: "1.25e-6",
				cachedInputPrice: "0.02e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.4-nano",
				deactivatedAt: new Date("2027-09-21"),
				inputPrice: "0.2e-6",
				outputPrice: "1.25e-6",
				cachedInputPrice: "0.02e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.5",
		name: "GPT-5.5",
		description:
			"A new class of intelligence for coding and professional work.",
		family: "openai",
		releasedAt: new Date("2026-04-23"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.5",
				serviceTiers: ["flex", "priority"],
				inputPrice: "5.0e-6",
				outputPrice: "30.0e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				verbosity: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.5",
				deactivatedAt: new Date("2027-10-26"),
				inputPrice: "5.0e-6",
				outputPrice: "30.0e-6",
				cachedInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.5-pro",
		name: "GPT-5.5 Pro",
		description:
			"Version of GPT-5.5 that uses more compute for smarter and more precise responses.",
		family: "openai",
		releasedAt: new Date("2026-04-23"),
		providers: [
			{
				providerId: "openai",
				test: "skip",
				externalId: "gpt-5.5-pro",
				serviceTiers: ["flex"],
				inputPrice: "30.0e-6",
				outputPrice: "180.0e-6",
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				verbosity: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.6-sol",
		name: "GPT-5.6 Sol",
		description:
			"Frontier GPT-5.6 model for complex professional work with the highest reasoning capabilities.",
		family: "openai",
		releasedAt: new Date("2026-07-09"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.6-sol",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "5.0e-6",
				outputPrice: "30.0e-6",
				cachedInputPrice: "0.5e-6",
				cacheWriteInputPrice: "6.25e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "5.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0.5e-6",
						cacheWriteInputPrice: "6.25e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "10.0e-6",
						outputPrice: "45.0e-6",
						cachedInputPrice: "1.0e-6",
						cacheWriteInputPrice: "12.5e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.6-sol",
				deactivatedAt: new Date("2028-01-11"),
				inputPrice: "5.0e-6",
				outputPrice: "30.0e-6",
				cachedInputPrice: "0.5e-6",
				cacheWriteInputPrice: "6.25e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "5.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0.5e-6",
						cacheWriteInputPrice: "6.25e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "10.0e-6",
						outputPrice: "45.0e-6",
						cachedInputPrice: "1.0e-6",
						cacheWriteInputPrice: "12.5e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				providerId: "aws-mantle",
				externalId: "openai.gpt-5.6-sol",
				// Sol is the one family member AWS has not deployed to us-west-2 —
				// that region 404s with "The model 'openai.gpt-5.6-sol' does not
				// exist". Pricing is identical across regions, so the entries carry
				// no per-region overrides.
				regions: [{ id: "us-east-1" }, { id: "us-east-2" }],
				// Mantle is in-region-only (the model cards list Geo and Global
				// cross-region as unsupported, and AWS lists global cross-region
				// pricing as "coming soon"), and AWS prices in-region inference at
				// OpenAI's data-residency tier — a flat 10% premium over the
				// standard first-party rates. Unlike the usual Bedrock geo/global
				// split there is no cheaper global rate to route to yet.
				// AWS displays the cache-write rate rounded to $6.88/M.
				inputPrice: "5.5e-6",
				outputPrice: "33.0e-6",
				cachedInputPrice: "0.55e-6",
				cacheWriteInputPrice: "6.875e-6",
				requestPrice: "0",
				// AWS caps the Mantle deployment at a 272K context (vs 1.05M
				// first-party). AWS's "272K" is 272 * 1024: upstream rejects prompts
				// of 278528 tokens or more. No pricingTiers because AWS publishes a
				// single flat rate for Mantle and does not expose OpenAI's
				// long-context tier at all.
				contextSize: 278528,
				maxOutput: 128000,
				streaming: true,
				// Bedrock Mantle only accepts data:/s3:// image URLs; the gateway
				// inlines remote http(s) image URLs as data URLs for this provider
				// (see prepare-request-body.ts).
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.6-terra",
		name: "GPT-5.6 Terra",
		description:
			"GPT-5.6 model that balances intelligence and cost for everyday workloads.",
		family: "openai",
		releasedAt: new Date("2026-07-09"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.6-terra",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "2.0e-6",
				outputPrice: "12.0e-6",
				cachedInputPrice: "0.2e-6",
				cacheWriteInputPrice: "2.5e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "2.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.2e-6",
						cacheWriteInputPrice: "2.5e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "18.0e-6",
						cachedInputPrice: "0.4e-6",
						cacheWriteInputPrice: "5.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.6-terra",
				deactivatedAt: new Date("2028-01-11"),
				inputPrice: "2.0e-6",
				outputPrice: "12.0e-6",
				cachedInputPrice: "0.2e-6",
				cacheWriteInputPrice: "2.5e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "2.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.2e-6",
						cacheWriteInputPrice: "2.5e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "18.0e-6",
						cachedInputPrice: "0.4e-6",
						cacheWriteInputPrice: "5.0e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				providerId: "aws-mantle",
				externalId: "openai.gpt-5.6-terra",
				// Pricing is identical across regions, so the entries carry no
				// per-region overrides.
				regions: [
					{ id: "us-east-1" },
					{ id: "us-east-2" },
					{ id: "us-west-2" },
				],
				// Mantle is in-region-only (the model cards list Geo and Global
				// cross-region as unsupported, and AWS lists global cross-region
				// pricing as "coming soon"), and AWS prices in-region inference at
				// OpenAI's data-residency tier — a flat 10% premium over the
				// standard first-party rates. Unlike the usual Bedrock geo/global
				// split there is no cheaper global rate to route to yet.
				inputPrice: "2.2e-6",
				outputPrice: "13.2e-6",
				cachedInputPrice: "0.22e-6",
				cacheWriteInputPrice: "2.75e-6",
				requestPrice: "0",
				// AWS caps the Mantle deployment at a 272K context (vs 1.05M
				// first-party). AWS's "272K" is 272 * 1024: upstream rejects prompts
				// of 278528 tokens or more. No pricingTiers because AWS publishes a
				// single flat rate for Mantle and does not expose OpenAI's
				// long-context tier at all.
				contextSize: 278528,
				maxOutput: 128000,
				streaming: true,
				// Bedrock Mantle only accepts data:/s3:// image URLs; the gateway
				// inlines remote http(s) image URLs as data URLs for this provider
				// (see prepare-request-body.ts).
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.6-luna",
		name: "GPT-5.6 Luna",
		description:
			"Cost-optimized GPT-5.6 model for high-volume tasks like classification and data extraction.",
		family: "openai",
		releasedAt: new Date("2026-07-09"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.6-luna",
				serviceTiers: ["flex", "priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "0.2e-6",
				outputPrice: "1.2e-6",
				cachedInputPrice: "0.02e-6",
				cacheWriteInputPrice: "0.25e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "0.2e-6",
						outputPrice: "1.2e-6",
						cachedInputPrice: "0.02e-6",
						cacheWriteInputPrice: "0.25e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.8e-6",
						cachedInputPrice: "0.04e-6",
						cacheWriteInputPrice: "0.5e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.6-luna",
				deactivatedAt: new Date("2028-01-11"),
				inputPrice: "0.2e-6",
				outputPrice: "1.2e-6",
				cachedInputPrice: "0.02e-6",
				cacheWriteInputPrice: "0.25e-6",
				pricingTiers: [
					{
						name: "Up to 272K",
						upToTokens: 272000,
						inputPrice: "0.2e-6",
						outputPrice: "1.2e-6",
						cachedInputPrice: "0.02e-6",
						cacheWriteInputPrice: "0.25e-6",
					},
					{
						name: "Over 272K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.8e-6",
						cachedInputPrice: "0.04e-6",
						cacheWriteInputPrice: "0.5e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1050000,
				maxOutput: 128000,
				streaming: true,
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
			{
				providerId: "aws-mantle",
				externalId: "openai.gpt-5.6-luna",
				// Pricing is identical across regions, so the entries carry no
				// per-region overrides.
				regions: [
					{ id: "us-east-1" },
					{ id: "us-east-2" },
					{ id: "us-west-2" },
				],
				// Mantle is in-region-only (the model cards list Geo and Global
				// cross-region as unsupported, and AWS lists global cross-region
				// pricing as "coming soon"), and AWS prices in-region inference at
				// OpenAI's data-residency tier — a flat 10% premium over the
				// standard first-party rates. Unlike the usual Bedrock geo/global
				// split there is no cheaper global rate to route to yet.
				inputPrice: "0.22e-6",
				outputPrice: "1.32e-6",
				cachedInputPrice: "0.022e-6",
				cacheWriteInputPrice: "0.275e-6",
				requestPrice: "0",
				// AWS caps the Mantle deployment at a 272K context (vs 1.05M
				// first-party). AWS's "272K" is 272 * 1024: upstream rejects prompts
				// of 278528 tokens or more. No pricingTiers because AWS publishes a
				// single flat rate for Mantle and does not expose OpenAI's
				// long-context tier at all.
				contextSize: 278528,
				maxOutput: 128000,
				streaming: true,
				// Bedrock Mantle only accepts data:/s3:// image URLs; the gateway
				// inlines remote http(s) image URLs as data URLs for this provider
				// (see prepare-request-body.ts).
				vision: true,
				tools: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "medium", "high", "xhigh", "max"],
				reasoningOutput: "omit",
				verbosity: true,
				supportsResponsesApi: true,
				jsonOutputSchema: true,
				supportedParameters: [
					"temperature",
					"top_p",
					"frequency_penalty",
					"presence_penalty",
					"response_format",
					"verbosity",
				],
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.2-codex",
		name: "GPT-5.2 Codex",
		description:
			"Upgraded codex model optimized for software engineering and agentic coding workflows with steerable reasoning.",
		family: "openai",
		releasedAt: new Date("2026-01-14"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.2-codex",
				// Shut down by OpenAI on 2026-07-23 (announced 2026-04-22);
				// requests now 404 with "has been deprecated". Replacement is
				// gpt-5.6-sol. The azure mapping below stays active — Azure runs
				// its own schedule and does not retire this until 2027-07-13.
				deactivatedAt: new Date("2026-07-23"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.2-codex",
				deactivatedAt: new Date("2027-07-13"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.3-codex",
		name: "GPT-5.3 Codex",
		description:
			"Most advanced agentic coding model combining frontier coding performance with broader reasoning capabilities.",
		family: "openai",
		releasedAt: new Date("2026-02-24"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.3-codex",
				serviceTiers: ["priority"],
				serviceTierMultipliers: { priority: 2 },
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				verbosity: true,
				jsonOutput: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.3-codex",
				deactivatedAt: new Date("2027-08-24"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 400000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh"],
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: true,
			},
		],
	},
	{
		id: "gpt-5.3-chat-latest",
		name: "GPT-5.3 Chat",
		description:
			"GPT-5.3 Chat delivers smoother conversations with better accuracy and fewer unnecessary refusals.",
		family: "openai",
		releasedAt: new Date("2026-03-03"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-5.3-chat-latest",
				deprecatedAt: new Date("2026-05-13"),
				deactivatedAt: new Date("2026-08-10"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: true,
				tools: true,
				webSearch: true,
				webSearchPrice: "0.01",
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: false,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-5.3-chat-latest",
				deprecatedAt: new Date("2026-05-13"),
				deactivatedAt: new Date("2026-08-10"),
				inputPrice: "1.75e-6",
				outputPrice: "14.0e-6",
				cachedInputPrice: "0.175e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 16384,
				streaming: true,
				reasoning: true,
				vision: true,
				tools: true,
				supportsResponsesApi: true,
				jsonOutputSchema: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "sora-2",
		name: "Sora 2",
		description:
			"OpenAI's standard Sora 2 video generation model for cinematic 720p clips.",
		family: "openai",
		output: ["video"],
		maxVideoDurationSeconds: 15,
		stability: "beta",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "openai",
				externalId: "sora-2",
				deactivatedAt: new Date("2026-03-24"),
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					"720p": "0.1",
				},
				supportedVideoSizes: ["1280x720", "720x1280"],
				supportedVideoDurationsSeconds: [4, 8, 12],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
			{
				providerId: "avalanche",
				externalId: "sora-2",
				deactivatedAt: new Date("2026-03-24"),
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					"720p": "0.08",
				},
				supportedVideoSizes: ["1280x720", "720x1280"],
				supportedVideoDurationsSeconds: [10],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "sora-2-pro",
		name: "Sora 2 Pro",
		description:
			"OpenAI's high-definition Sora 2 model with larger portrait and landscape outputs.",
		family: "openai",
		output: ["video"],
		maxVideoDurationSeconds: 15,
		stability: "beta",
		releasedAt: new Date("2025-12-08"),
		providers: [
			{
				providerId: "openai",
				externalId: "sora-2-pro",
				deactivatedAt: new Date("2026-03-24"),
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					"720p": "0.3",
					hd: "0.5",
					"1080p": "0.7",
				},
				supportedVideoSizes: [
					"1280x720",
					"720x1280",
					"1792x1024",
					"1024x1792",
					"1920x1080",
					"1080x1920",
				],
				supportedVideoDurationsSeconds: [4, 8, 12],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
			{
				providerId: "avalanche",
				externalId: "sora-2-pro",
				deactivatedAt: new Date("2026-03-24"),
				inputPrice: undefined,
				outputPrice: undefined,
				requestPrice: undefined,
				perSecondPrice: {
					"720p": "0.24",
					hd: "0.4",
				},
				supportedVideoSizes: ["1280x720", "720x1280", "1792x1024", "1024x1792"],
				supportedVideoDurationsSeconds: [10],
				contextSize: 32768,
				maxOutput: 1,
				streaming: false,
				tools: false,
				jsonOutput: false,
				jsonOutputSchema: false,
				videoGenerations: true,
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "gpt-image-2",
		name: "GPT Image 2",
		description:
			"OpenAI's next-generation image model with improved quality and prompt adherence, supporting text and image inputs.",
		family: "openai",
		output: ["image"],
		releasedAt: new Date("2026-04-21"),
		providers: [
			{
				test: "skip",
				providerId: "openai",
				externalId: "gpt-image-2",
				inputPrice: "5e-6",
				outputPrice: "0",
				cachedInputPrice: "1.25e-6",
				imageInputPrice: "8e-6",
				cachedImageInputPrice: "2e-6",
				imageOutputPrice: "30e-6",
				requestPrice: "0",
				contextSize: 272000,
				maxOutput: 128000,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
			{
				test: "skip",
				providerId: "azure",
				externalId: "gpt-image-2",
				deactivatedAt: new Date("2027-10-21"),
				inputPrice: "5e-6",
				outputPrice: "0",
				cachedInputPrice: "1.25e-6",
				imageInputPrice: "8e-6",
				cachedImageInputPrice: "2e-6",
				imageOutputPrice: "30e-6",
				requestPrice: "0",
				contextSize: 272000,
				maxOutput: 128000,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "text-embedding-3-small",
		name: "Text Embedding 3 Small",
		description:
			"Small, efficient embedding model. Default output dimension 1536; supports the `dimensions` parameter to shorten output.",
		family: "openai",
		output: ["embedding"],
		releasedAt: new Date("2024-01-25"),
		providers: [
			{
				providerId: "openai",
				externalId: "text-embedding-3-small",
				inputPrice: "0.02e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 8192,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "text-embedding-3-large",
		name: "Text Embedding 3 Large",
		description:
			"Highest-quality OpenAI embedding model. Default output dimension 3072; supports the `dimensions` parameter to shorten output (OpenAI examples: 256, 1024).",
		family: "openai",
		output: ["embedding"],
		releasedAt: new Date("2024-01-25"),
		providers: [
			{
				providerId: "openai",
				externalId: "text-embedding-3-large",
				inputPrice: "0.13e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 8192,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "text-embedding-ada-002",
		name: "Text Embedding Ada 002",
		description:
			"Legacy OpenAI embedding model with a fixed 1536-dimensional output. Does not support the `dimensions` parameter.",
		family: "openai",
		output: ["embedding"],
		releasedAt: new Date("2022-12-15"),
		providers: [
			{
				providerId: "openai",
				externalId: "text-embedding-ada-002",
				inputPrice: "0.1e-6",
				outputPrice: "0",
				requestPrice: "0",
				contextSize: 8192,
				streaming: false,
				tools: false,
				jsonOutput: false,
				embeddings: true,
			},
		],
	},
	{
		id: "tts-1",
		name: "TTS-1",
		description:
			"OpenAI text-to-speech model optimized for real-time use. Generates speech via the /v1/audio/speech endpoint.",
		family: "openai",
		output: ["audio"],
		releasedAt: new Date("2023-11-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "tts-1",
				inputPrice: "0",
				outputPrice: "0",
				inputCharacterPrice: "15e-6",
				requestPrice: "0",
				contextSize: 4096,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: OPENAI_TTS_VOICES,
			},
		],
	},
	{
		id: "tts-1-hd",
		name: "TTS-1 HD",
		description:
			"OpenAI text-to-speech model optimized for quality. Generates speech via the /v1/audio/speech endpoint.",
		family: "openai",
		output: ["audio"],
		releasedAt: new Date("2023-11-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "tts-1-hd",
				inputPrice: "0",
				outputPrice: "0",
				inputCharacterPrice: "30e-6",
				requestPrice: "0",
				contextSize: 4096,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: OPENAI_TTS_VOICES,
			},
		],
	},
	{
		id: "gpt-4o-mini-tts",
		name: "GPT-4o Mini TTS",
		description:
			"OpenAI text-to-speech model with steerable delivery via instructions. Generates speech via the /v1/audio/speech endpoint.",
		family: "openai",
		output: ["audio"],
		releasedAt: new Date("2025-03-20"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-mini-tts",
				// Token-billed; the gateway requests stream_format=sse so the
				// speech.audio.done event reports usage (the binary response has none).
				inputPrice: "0.6e-6",
				outputPrice: "12.0e-6",
				outputAudioPrice: "12.0e-6",
				requestPrice: "0",
				contextSize: 2000,
				streaming: false,
				tools: false,
				jsonOutput: false,
				speechGenerations: true,
				supportedVoices: OPENAI_GPT4O_MINI_TTS_VOICES,
			},
		],
	},
	{
		id: "gpt-realtime",
		name: "GPT Realtime",
		aliases: ["gpt-realtime-2025-08-28"],
		description:
			"Legacy OpenAI speech-to-speech realtime model, superseded by gpt-realtime-2.1. Served via the gateway's /v1/realtime WebSocket endpoint with text and audio input/output and function calling.",
		family: "openai",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2025-08-28"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-realtime",
				inputPrice: "4.0e-6",
				cachedInputPrice: "0.4e-6",
				outputPrice: "16.0e-6",
				inputAudioPrice: "32.0e-6",
				cachedInputAudioPrice: "0.4e-6",
				outputAudioPrice: "64.0e-6",
				imageInputPrice: "5.0e-6",
				cachedImageInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: OPENAI_REALTIME_VOICES,
				test: "skip",
				// OpenAI announced deprecation of the legacy realtime family on
				// 2026-07-20 with removal from the API on 2027-01-20.
				deprecatedAt: new Date("2026-07-20"),
				deactivatedAt: new Date("2027-01-20"),
			},
		],
	},
	{
		id: "gpt-realtime-mini",
		name: "GPT Realtime Mini",
		aliases: ["gpt-realtime-mini-2025-10-06"],
		description:
			"Legacy cost-efficient OpenAI speech-to-speech realtime model, superseded by gpt-realtime-2.1-mini. Served via the gateway's /v1/realtime WebSocket endpoint with text and audio input/output and function calling.",
		family: "openai",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2025-10-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-realtime-mini",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "2.4e-6",
				inputAudioPrice: "10.0e-6",
				cachedInputAudioPrice: "0.3e-6",
				outputAudioPrice: "20.0e-6",
				requestPrice: "0",
				contextSize: 32768,
				maxOutput: 4096,
				streaming: false,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: OPENAI_REALTIME_VOICES,
				test: "skip",
				// OpenAI announced deprecation of the legacy realtime family on
				// 2026-07-20 with removal from the API on 2027-01-20.
				deprecatedAt: new Date("2026-07-20"),
				deactivatedAt: new Date("2027-01-20"),
			},
		],
	},
	{
		id: "gpt-realtime-2.1",
		name: "GPT Realtime 2.1",
		description:
			"OpenAI's current speech-to-speech realtime model. Served via the gateway's /v1/realtime WebSocket endpoint with text and audio input/output and function calling.",
		family: "openai",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2026-07-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-realtime-2.1",
				inputPrice: "4.0e-6",
				cachedInputPrice: "0.4e-6",
				outputPrice: "24.0e-6",
				inputAudioPrice: "32.0e-6",
				cachedInputAudioPrice: "0.4e-6",
				outputAudioPrice: "64.0e-6",
				imageInputPrice: "5.0e-6",
				cachedImageInputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 32000,
				streaming: false,
				vision: false,
				audio: true,
				reasoning: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: OPENAI_REALTIME_VOICES,
				test: "skip",
			},
		],
	},
	{
		id: "gpt-realtime-2.1-mini",
		name: "GPT Realtime 2.1 Mini",
		description:
			"Cost-efficient variant of OpenAI's current speech-to-speech realtime model. Served via the gateway's /v1/realtime WebSocket endpoint with text and audio input/output and function calling.",
		family: "openai",
		output: ["text", "audio"],
		stability: "beta",
		releasedAt: new Date("2026-07-06"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-realtime-2.1-mini",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "2.4e-6",
				inputAudioPrice: "10.0e-6",
				cachedInputAudioPrice: "0.3e-6",
				outputAudioPrice: "20.0e-6",
				imageInputPrice: "0.8e-6",
				cachedImageInputPrice: "0.08e-6",
				requestPrice: "0",
				contextSize: 128000,
				maxOutput: 32000,
				streaming: false,
				vision: false,
				audio: true,
				tools: true,
				jsonOutput: false,
				realtime: true,
				supportedVoices: OPENAI_REALTIME_VOICES,
				test: "skip",
			},
		],
	},
	{
		id: "gpt-4o-mini-transcribe",
		name: "GPT-4o Mini Transcribe",
		description:
			"Cost-efficient token-metered OpenAI speech-to-text model. Used as the default input-audio transcription model for realtime sessions on the gateway's /v1/realtime WebSocket endpoint.",
		family: "openai",
		output: ["text"],
		stability: "beta",
		releasedAt: new Date("2025-03-20"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-mini-transcribe",
				inputPrice: "1.25e-6",
				outputPrice: "5.0e-6",
				inputAudioPrice: "3.0e-6",
				requestPrice: "0",
				contextSize: 16000,
				maxOutput: 2000,
				streaming: false,
				vision: false,
				audio: true,
				tools: false,
				jsonOutput: false,
				realtimeTranscription: true,
				test: "skip",
			},
		],
	},
	{
		id: "gpt-4o-transcribe",
		name: "GPT-4o Transcribe",
		description:
			"Token-metered OpenAI speech-to-text model. Used as the input-audio transcription model for realtime sessions on the gateway's /v1/realtime WebSocket endpoint.",
		family: "openai",
		output: ["text"],
		stability: "beta",
		releasedAt: new Date("2025-03-20"),
		providers: [
			{
				providerId: "openai",
				externalId: "gpt-4o-transcribe",
				inputPrice: "2.5e-6",
				outputPrice: "10.0e-6",
				inputAudioPrice: "6.0e-6",
				requestPrice: "0",
				contextSize: 16000,
				maxOutput: 2000,
				streaming: false,
				vision: false,
				audio: true,
				tools: false,
				jsonOutput: false,
				realtimeTranscription: true,
				test: "skip",
			},
		],
	},
] as const satisfies ModelDefinition[];
